import * as React from 'react';

import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FreeTrialBannerProps {
  /** The primary action label, e.g. "Choose a plan". */
  buttonLabel: string;
  /** Optional extra CSS classes merged onto the outer wrapper via `cn()`. */
  className?: string;
  /** Optional brand mark rendered before the message. */
  logo?: React.ReactNode;
  /** The already-translated message, e.g. "Free trial: 12 days left out of 30." */
  message: React.ReactNode;
  /** Called when the primary action button is clicked. */
  onChoosePlan: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Inset free-trial reminder strip, mounted above page content for the whole trial period.
 * Presentational only — visibility, message, and day counts are resolved by the caller.
 * Never dismissible by design (no close affordance).
 */
export function FreeTrialBanner({
  buttonLabel,
  className,
  logo,
  message,
  onChoosePlan,
}: FreeTrialBannerProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'mx-4 mt-2 flex shrink-0 flex-wrap items-center rounded-md border border-primary/20 bg-gradient-to-r from-primary/10 to-transparent to-65% rtl:bg-gradient-to-l md:mx-6',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-1.5">
        {logo}
        <p className="min-w-[180px] text-xs text-foreground">{message}</p>
        <Button className="h-6 rounded px-2.5 text-xs" onClick={onChoosePlan} size="sm" type="button">
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
